import { useState, useEffect, useCallback } from 'react';
import { RefreshCw, Search } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { EmptyState, PageHeader, SectionCard } from '@/components/admin/page';
import { api, ApiError } from '../lib/api';
import { displayDeviceName, displayIdentifier, formatDateTime } from '../lib/format';
import { useI18n } from '../lib/i18n';
import { Pager } from '../lib/Pager';

const PAGE_SIZE = 20;

interface DeviceRow {
  id: string;
  tenantId: string;
  tenantName: string | null;
  userId: string | null;
  userEmail: string | null;
  apiKeyId: string | null;
  deviceName: string | null;
  platform: string | null;
  status: string;
  lastSeenAt: string | null;
  revokedAt: string | null;
  lockedAt: string | null;
  wipeRequestedAt: string | null;
  wipeConfirmedAt: string | null;
  createdAt: string;
}

interface ListResponse {
  devices: DeviceRow[];
  total: number;
  limit: number;
  offset: number;
  count: number;
}

type StatusFilter = 'all' | 'active' | 'revoked' | 'locked';

const STATUS_FILTER_OPTIONS: { value: StatusFilter; labelKey: string }[] = [
  { value: 'all', labelKey: 'devices.filter.all' },
  { value: 'active', labelKey: 'status.active' },
  { value: 'revoked', labelKey: 'status.revoked' },
  { value: 'locked', labelKey: 'status.locked' },
];

export function DevicesPage() {
  const { t } = useI18n();
  const [rows, setRows] = useState<DeviceRow[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [q, setQ] = useState('');
  const [committedQ, setCommittedQ] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [actingId, setActingId] = useState<string | null>(null);

  const buildParams = useCallback((overridePage?: number) => {
    const params = new URLSearchParams();
    if (committedQ) params.set('q', committedQ);
    params.set('limit', String(PAGE_SIZE));
    params.set('offset', String((overridePage ?? page) * PAGE_SIZE));
    if (statusFilter !== 'all') params.set('status', statusFilter);
    return params;
  }, [page, statusFilter, committedQ]);

  const fetchDevices = useCallback(async (overridePage?: number) => {
    setLoading(true);
    setError(null);
    try {
      const r = await api<ListResponse>(`/v1/admin/devices?${buildParams(overridePage)}`);
      setRows(r.devices);
      setTotal(r.total);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : (e as Error).message);
    } finally {
      setLoading(false);
    }
  }, [buildParams]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    api<ListResponse>(`/v1/admin/devices?${buildParams()}`)
      .then((r) => {
        if (cancelled) return;
        setRows(r.devices);
        setTotal(r.total);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e instanceof ApiError ? e.message : (e as Error).message);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, statusFilter, committedQ]);

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handleFilterChange = (value: StatusFilter) => {
    setStatusFilter(value);
    setPage(0);
  };

  const search = () => { setPage(0); setCommittedQ(q.trim()); };

  const deviceAction = async (
    device: DeviceRow,
    action: 'revoke' | 'lock' | 'wipe',
  ) => {
    const labelKey =
      action === 'revoke' ? 'devices.action.revoke' :
      action === 'lock' ? 'devices.action.lock' :
      'devices.action.wipe';
    if (!confirm(t('devices.action.confirm', { label: t(labelKey) }))) return;
    setActingId(device.id);
    try {
      await api(`/v1/admin/devices/${device.id}/${action}`, { method: 'POST' });
      await fetchDevices();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : (e as Error).message);
    } finally {
      setActingId(null);
    }
  };

  const statusBadge = (status: string) => {
    const variant =
      status === 'active' || status === 'online'
        ? 'success'
        : status === 'revoked'
          ? 'destructive'
          : status === 'locked' || status === 'wipe_pending'
            ? 'warning'
            : 'muted';
    const label = t(`status.${status}`) !== `status.${status}` ? t(`status.${status}`) : status;
    return <Badge variant={variant}>{label}</Badge>;
  };

  const isRevoked = (d: DeviceRow) => d.status === 'revoked' || d.revokedAt !== null;

  return (
    <div>
      <PageHeader
        title={t('devices.admin.title')}
        description={t('devices.admin.desc')}
        actions={
          <Button variant="outline" onClick={() => fetchDevices()} disabled={loading}>
            <RefreshCw className={loading ? 'animate-spin' : ''} />
            {loading ? t('common.refreshing') : t('common.refresh')}
          </Button>
        }
      />

      {error && (
        <Alert variant="destructive" className="mb-4">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <SectionCard
        title={t('devices.admin.section', { n: total })}
        description={t('devices.admin.sectionDesc')}
      >
        <div className="mb-3 flex gap-2 flex-wrap">
          {STATUS_FILTER_OPTIONS.map((opt) => (
            <Button
              key={opt.value}
              variant={statusFilter === opt.value ? 'default' : 'outline'}
              size="sm"
              onClick={() => handleFilterChange(opt.value)}
            >
              {t(opt.labelKey)}
            </Button>
          ))}
        </div>

        <div className="mb-3 flex flex-col gap-3 sm:flex-row">
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') search(); }}
            placeholder={t('devices.searchPh')}
            className="flex-1"
          />
          <Button onClick={search} disabled={loading}><Search />{loading ? t('common.searching') : t('common.search')}</Button>
        </div>

        {rows.length === 0 && !loading ? (
          <EmptyState>{t('devices.admin.empty')}</EmptyState>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('devices.col.deviceName')}</TableHead>
                <TableHead>{t('devices.col.platform')}</TableHead>
                <TableHead>{t('common.status')}</TableHead>
                <TableHead>{t('devices.col.workspace')}</TableHead>
                <TableHead>{t('common.user')}</TableHead>
                <TableHead>{t('devices.col.apiKeyId')}</TableHead>
                <TableHead>{t('common.lastSeen')}</TableHead>
                <TableHead>{t('common.created')}</TableHead>
                <TableHead className="text-right">{t('common.actions')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((d) => (
                <TableRow key={d.id}>
                  <TableCell className="font-medium">
                    {displayDeviceName(d.deviceName) ?? displayIdentifier(d.id, t('common.device'))}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {d.platform ?? <span className="text-muted-foreground">—</span>}
                  </TableCell>
                  <TableCell>{statusBadge(d.status)}</TableCell>
                  <TableCell className="text-muted-foreground">
                    {d.tenantName ?? <span className="text-muted-foreground">—</span>}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {d.userEmail ?? <span className="text-muted-foreground">—</span>}
                  </TableCell>
                  <TableCell className="text-muted-foreground font-mono text-xs">
                    {d.apiKeyId
                      ? displayIdentifier(d.apiKeyId.slice(0, 8), t('common.prefix'))
                      : <span className="text-muted-foreground">—</span>}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {formatDateTime(d.lastSeenAt)}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {formatDateTime(d.createdAt)}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      {!isRevoked(d) && (
                        <Button
                          variant="destructive"
                          size="sm"
                          disabled={actingId === d.id}
                          onClick={() => deviceAction(d, 'revoke')}
                        >
                          {t('devices.action.revoke')}
                        </Button>
                      )}
                      <Button
                        variant="warning"
                        size="sm"
                        disabled={actingId === d.id}
                        onClick={() => deviceAction(d, 'lock')}
                      >
                        {t('devices.action.lock')}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={actingId === d.id}
                        onClick={() => deviceAction(d, 'wipe')}
                      >
                        {t('devices.action.wipe')}
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
        <Pager page={page} pageSize={PAGE_SIZE} total={total} onChange={handlePageChange} />
      </SectionCard>
    </div>
  );
}
